/**
 * TypeScript interfaces for different exception domains
 */

// Base exception metadata shared across all types
export interface ExceptionMetadata {
  exception_detected_at?: string
  exception_severity?: 'warning' | 'error' | 'critical'
  exception_metadata?: Record<string, any>
}

// Register-specific exception
export interface RegisterException extends ExceptionMetadata {
  ls_id: string
  register_name: string
  invoice_prefix?: string
  outlet_id?: string
  outlet_name: string
  is_open: boolean
  register_open_time?: string
  register_close_time?: string
  register_open_sequence_id?: string
  days_open?: number
  cutoff_days?: number
  lightspeed_outlet_edit_link?: string
  lightspeed_register_close_link?: string
  lightspeed_register_closure_link?: string
}

// Outlet-specific exception
export interface OutletException extends ExceptionMetadata {
  ls_id: string
  outlet_name: string
  is_dnu?: boolean
  physical_address_1?: string
  physical_suburb?: string
  physical_city?: string
  physical_postcode?: string
  physical_state?: string
  phone?: string
  email?: string
  register_count: number
  sales_count?: number
  last_sale_date?: string
  lightspeed_outlet_edit_link?: string
}

// Sales exception
export interface SalesException extends ExceptionMetadata {
  ls_id: string
  invoice_number: string
  sale_date: string
  customer_id?: string
  customer_first_name?: string
  customer_last_name?: string
  customer_company?: string
  status: string
  ls_total_amount: number
  payments_total: number
  payment_count: number
  discrepancy?: number
  accounts_transaction_id?: string
  lightspeed_sale_link?: string
  lightspeed_ledger_link?: string
  lightspeed_customer_link?: string
  xero_invoice_link?: string
}

// Inventory exception
export interface InventoryException extends ExceptionMetadata {
  ls_id: string
  name: string
  sku?: string
  handle?: string
  price_including_tax?: number
  supply_price?: number
  account_code?: string
  account_code_purchase?: string
  has_sales_account?: boolean
  has_purchase_account?: boolean
  lightspeed_product_link?: string
  outlet_id?: string
  outlet_name?: string
  outlet_costs?: any[]
}

// API Response types
export interface RegisterExceptionResponse {
  systems: RegisterException[]
  total: number
  limit: number
  offset: number
  days_filter?: number
}

export interface OutletExceptionResponse {
  outlets: OutletException[]
  total: number
  limit: number
  offset: number
}

export interface SalesExceptionResponse {
  sales: SalesException[]
  total: number
  limit: number
  offset: number
}

export interface InventoryExceptionResponse {
  inventory: InventoryException[]
  total: number
  limit: number
  offset: number
  outlets?: Array<{ id: string; name: string }>
}

// Exception data domain (backend uses 'sale'; some clients historically pass 'sales')
export type ExceptionDataDomain =
  | 'sale'
  | 'stock_management'
  | 'product_setup'
  | 'product_pricing'
  | 'system'
  | 'client'
  | 'accounting'

// Exception type metadata
export interface ExceptionType {
  id: number
  code: string
  name: string
  name_short: string
  why_flagged?: string
  overview?: string
  search_keywords?: string[]
  is_active: boolean
  data_domain: ExceptionDataDomain
  count: number
  display_columns?: string[]
  health_score?: {
    band: string
    color: string
    score?: number
    metrics?: {
      count: number
      value?: number
      thresholds?: {
        good?: number
        watch?: number
        poor?: number
      }
    }
  }
  /**
   * Per-type review rules from ``exception_types.action_policy``. Today just
   * ``{ allow_ignore }``; absent/undefined means permissive. Read via
   * ``allowIgnore()`` rather than touched directly.
   */
  action_policy?: {
    allow_ignore?: boolean
  }
}

/**
 * Whether an exception type permits the "Ignore" outcome. Permissive
 * by default (only an explicit ``false`` disallows it) - mirrors the backend
 * ``shared/exception_queries/action_policy.py::allow_ignore``.
 */
export function allowIgnore(
  type?: { action_policy?: { allow_ignore?: boolean } | null } | null,
): boolean {
  return type?.action_policy?.allow_ignore !== false
}

// Generic response type that can be used when we don't know the exact structure
export type SystemExceptionResponse = RegisterExceptionResponse | OutletExceptionResponse